import { shortLanguages } from "./languages";

const notification = {message: 'Something failed!', description: 'The file name is wrong!', type: 'error'};

const videoNameNotification = {
	message: 'Invalid file name',
	description: 'Expected format: {referenceID}[{lang}-{res}].mp4 or .mov (e.g. ABC123[en-HD].mp4, ABC123[es-LOW].mov)',
	type: 'error',
};

const VIDEO_RESOLUTIONS = ['8k', 'uhd', 'hd', 'high', 'low'];

const subtitleNameNotification = {
	message: 'Invalid file name',
	description: 'Expected format: {referenceID}[{lang}-subt].srt or .vtt (e.g. ABC123[en-subt].srt)',
	type: 'error',
};

const videoThumbnailNameNotification = {
	message: 'Invalid file name',
	description: 'Expected format: {referenceID}[thumb].jpg or .jpeg (e.g. ABC123[thumb].jpg)',
	type: 'error',
};

export function checkAudioNames ({id, openNotification}) {
	return (file) => {
		const name = removeExtension(file.name);
		if( !checkAudioFormat(name, id) ) {
			openNotification(notification);
			return false;
		}
		const [lang, audio] = getParamsBetweenBrackets(name);

		if (!langExist(lang)) {
			openNotification(notification);
			return false;
		}

		if (audio !== 'audio') {
			openNotification(notification);
			return false;
		}

		return true;
	};
}

export function checkPhotoNames ({openNotification}) {
	return (file) => {
		// Check file extension: only .jpg, .jpeg, .JPG, .JPEG allowed
		const extension = file.name.split('.').pop()?.toLowerCase();
		if (!['jpg', 'jpeg'].includes(extension)) {
			openNotification(notification);
			return false;
		}

		const name = removeExtension(file.name);
		// Check filename only contains letters, numbers, hyphen, and underscore
		const filenameRegex = /^[a-zA-Z0-9_-]+$/;
		if (!filenameRegex.test(name)) {
			openNotification(notification);
			return false;
		}

		return true;
	};
}

export function checkVideoNames ({id, openNotification, language}) {
	return (file) => {
		const extension = file.name.split('.').pop()?.toLowerCase();
		if (!['mp4', 'mov'].includes(extension)) {
			openNotification({
				...videoNameNotification,
				description: 'Only .mp4 and .mov files are allowed.',
			});
			return false;
		}

		const name = removeExtension(file.name);
		if (!checkVideoFormat(name, id)) {
			openNotification(videoNameNotification);
			return false;
		}

		const [lang, res] = getParamsBetweenBrackets(name);
		if (!langExist(lang)) {
			openNotification(videoNameNotification);
			return false;
		}

		if (language) {
			const languageIsoCode = getLangFromIsoCode(language);
			if (lang.toLowerCase() !== languageIsoCode) {
				openNotification(videoNameNotification);
				return false;
			}
		}

		if (!VIDEO_RESOLUTIONS.includes(res?.toLowerCase())) {
			openNotification({
				...videoNameNotification,
				description: 'Resolution must be one of: 8K, UHD, HD, HIGH, LOW (e.g. ABC123[en-HD].mp4).',
			});
			return false;
		}

		return true;
	};
}

export function checkSubtitleNames ({id, openNotification, language}) {
	return (file) => {
		const extension = file.name.split('.').pop()?.toLowerCase();
		if (!['srt', 'vtt'].includes(extension)) {
			openNotification({
				...subtitleNameNotification,
				description: 'Only .srt and .vtt files are allowed.',
			});
			return false;
		}

		const name = removeExtension(file.name);
		if (!checkVideoFormat(name, id)) {
			openNotification(subtitleNameNotification);
			return false;
		}

		const [lang, subtitleType] = getParamsBetweenBrackets(name);
		if (!langExist(lang)) {
			openNotification(subtitleNameNotification);
			return false;
		}

		if (language) {
			const languageIsoCode = getLangFromIsoCode(language);
			if (lang.toLowerCase() !== languageIsoCode) {
				openNotification(subtitleNameNotification);
				return false;
			}
		}

		if (subtitleType !== 'subt') {
			openNotification(subtitleNameNotification);
			return false;
		}

		return true;
	};
}

export function checkVideoThumbnailNames ({id, openNotification}) {
	return (file) => {
		const extension = file.name.split('.').pop()?.toLowerCase();
		if (!['jpg', 'jpeg'].includes(extension)) {
			openNotification({
				...videoThumbnailNameNotification,
				description: 'Only .jpg and .jpeg files are allowed.',
			});
			return false;
		}

		const name = removeExtension(file.name);
		if (!checkVideoThumbnailFormat(name, id)) {
			openNotification(videoThumbnailNameNotification);
			return false;
		}

		return true;
	};
}

export function checkThumbnailsNames ({id, openNotification}) {
	return (file) => {
		const name = removeExtension(file.name);
		if( !checkThumbnailFormat(name, id) ) {
			openNotification(notification);
			return false;
		}

		return true;
	};
}

export function checkInfograficNames ({id, openNotification, language}) {
	return (file) => {
    const name = removeExtension(file.name);
		if( !checkInfographicFormat(name, id) ) {
			openNotification(notification);
			return false;
		}
		const [lang, infographics] = getParamsBetweenBrackets(name);
		const languageIsoCode = getLangFromIsoCode(language);

		if (!langExist(lang)) {
			openNotification(notification);
			return false;
		}

		if (language && lang !== languageIsoCode) {
			openNotification(notification);
			return false;
		}

		if (infographics !== 'infographics') {
			openNotification(notification);
			return false;
		}

		return true;
    };
}

export function nameIsEpid ({id, openNotification}) {
	return (file) => {
        const name = removeExtension(file.name);
		if( name !== id ) {
			openNotification(notification);
			return false;
		}

		return true;
    };
}


const removeExtension = (name) => {
	return name.split('.')[0];
};

const checkAudioFormat = (name, id) => {
	if (!id || id === '' ) {
		return false;
	}
	const regex = new RegExp(id + '\\[[^\\]]*\\]', 'i');
	return regex.test(name);
};

const checkVideoFormat = (name, id) => {
	if (!id || id === '') {
		return false;
	}
	const regex = new RegExp('^' + id + '\\[[^\\]]+\\]$', 'i');
	return regex.test(name);
};

const checkVideoThumbnailFormat = (name, id) => {
	if (!id || id === '') {
		return false;
	}
	const regex = new RegExp('^' + id + '\\[thumb\\]$', 'i');
	return regex.test(name);
};

const checkInfographicFormat = (name, id) => {
	if (!id || id === '' ) {
		return false;
	}
	const regex = new RegExp(id + '_\\d\\d\\d\\[[^\\]]*\\]', 'i');
	return regex.test(name);
};

const checkThumbnailFormat = (name, id) => {
	if (!id || id === '' ) {
		return false;
	}
	
	const parts = name.split('_');

	const thumbnail = parts.pop();
	const lang = parts.pop();
	const thisId = parts.join('_');

	if ( thisId != id || !langExist(lang) || thumbnail !== 'thumbnail') {
		return false;
	}

	return true;
	
};

export const getParamsBetweenBrackets = (name) => {
	return name.match(/\[([^)]+)]*\]/)?.[1].split('-') || [];
};

const langExist = (lang) => {
	if (shortLanguages.includes(lang.toLowerCase())) {
		return true;
	}
	
	return false;
};

export const getLang = (name) => {
	const params = getParamsBetweenBrackets(name);

	if (params.length > 0) {
		return params[0];
	}

	const parts = name.split('_');

	return parts[parts.length - 2];
};

export function isImageFile(str) {
    // Regex to check valid
    // Image File
    let regex = new RegExp(/[^\s]+(.*?).(jpg|jpeg|png|gif|JPG|JPEG|PNG|GIF)$/);
 
    // if str
    // is empty return false
    if (str == null) {
        return "false";
    }
 
    // Return true if the str
    // matched the ReGex
    if (regex.test(str) == true) {
        return "true";
    }
    else {
        return "false";
    }
}

export function getCategoryName(string) {
	//urn:justskos:ep:scheme:{value}

	const value = string.replace('urn:justskos:ep:scheme:', '');
	const name = value.replace('_', ' ');

	return name;
}

const getLangFromIsoCode = (isoCode) => {
	return isoCode.split('_')[0].toLowerCase();
};
